import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { useSeo, Reveal, Laptop, Phone, DemoButton, Eyebrow, H2 } from './site/ui';
import { Faq, Closing } from './site/HomeSections2';
import { FEATURES, featureBySlug } from './site/features';

const FeatureHero = ({ f }) => (
  <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-32 sm:px-8 lg:grid-cols-[1fr_1.15fr]" data-testid="feature-hero">
    <Reveal>
      <Eyebrow>{f.name}</Eyebrow>
      <h1 className="dl-display mt-4 text-4xl font-semibold leading-[1.04] tracking-tight text-[#0f172a] sm:text-5xl lg:text-6xl" data-testid="feature-title">{f.headline}</h1>
      <p className="mt-5 max-w-xl text-base text-[#475569] md:text-lg">{f.sub}</p>
      <DemoButton className="mt-8" testid="feature-hero-book-demo" />
    </Reveal>
    <Reveal delay={150}><Laptop src={f.screen} alt={`${f.name} in Driveline`} /></Reveal>
  </section>
);

const Benefit = ({ b, i }) => (
  <div className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`} data-testid={`feature-benefit-${i}`}>
    <Reveal className={b.phone ? 'flex justify-center' : ''}>
      {b.phone ? <Phone src={b.screen} alt={b.title} className="h-[460px] w-[230px]" /> : <Laptop src={b.screen} alt={b.title} />}
    </Reveal>
    <Reveal delay={120}>
      <span className="dl-display text-sm font-semibold text-[#0f766e]">0{i + 1}</span>
      <h3 className="dl-display mt-2 text-3xl font-semibold text-[#0f172a]">{b.title}</h3>
      <p className="mt-4 flex gap-2 text-[15px] text-[#334155]"><Check size={18} className="mt-0.5 shrink-0 text-[#0f766e]" />{b.line}</p>
    </Reveal>
  </div>
);

const Benefits = ({ f }) => (
  <section className="bg-[#efece4] py-20 sm:py-28" data-testid="feature-benefits">
    <div className="mx-auto max-w-7xl space-y-20 px-5 sm:px-8">
      {f.benefits.map((b, i) => <Benefit key={b.title} b={b} i={i} />)}
    </div>
  </section>
);

const Steps = ({ f }) => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="feature-steps">
    <Eyebrow>How it works</Eyebrow>
    <H2>{f.steps.length} steps, no training course.</H2>
    <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {f.steps.map((s, i) => (
        <Reveal as="li" key={s} delay={i * 90} className="rounded-3xl bg-[#ffffff] p-6" data-testid={`feature-step-${i}`}>
          <span className="dl-display flex h-9 w-9 items-center justify-center rounded-full bg-[#0f172a] text-sm font-semibold text-[#f8fafc]">{i + 1}</span>
          <p className="mt-5 text-[15px] font-semibold leading-snug text-[#0f172a]">{s}</p>
        </Reveal>
      ))}
    </ol>
  </section>
);

const MoreFeatures = ({ f }) => (
  <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8" data-testid="feature-more">
    <Eyebrow>Also in Driveline</Eyebrow>
    <div className="mt-6 flex flex-wrap gap-2">
      {FEATURES.filter((x) => x.slug !== f.slug).map((x) => (
        <Link key={x.slug} to={`/driveline/features/${x.slug}`} data-testid={`feature-more-${x.slug}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#d6d3cb] bg-[#ffffff] px-4 py-2 text-sm font-semibold text-[#0f172a] transition-colors hover:border-[#0f766e] hover:text-[#0f766e]">
          {x.name} <ArrowRight size={14} />
        </Link>
      ))}
    </div>
  </section>
);

export default function FeaturePage() {
  const { slug } = useParams();
  const f = featureBySlug(slug);
  useSeo(f ? { ...f.seo, image: f.screen } : { title: 'Driveline', description: '' });
  if (!f) return <Navigate to="/driveline" replace />;
  return (
    <div data-testid={`feature-page-${f.slug}`}>
      <FeatureHero f={f} />
      <Benefits f={f} />
      <Steps f={f} />
      <div className="bg-[#efece4]"><Faq items={f.faq} title={`${f.name}: your questions.`} /></div>
      <MoreFeatures f={f} />
      <Closing title={`See ${f.name} on your own workshop.`} />
    </div>
  );
}
